import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, HeartHandshake, MessageSquareText, ShieldCheck, MapPin } from "lucide-react";
import SEO from "@/components/SEO";
import Reveal, { EASE } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import { founders } from "@/data/founders";

const MotionLink = motion.create(Link);

const values = [
  { icon: MessageSquareText, title: "Plain English, always", desc: "If we can't explain it without jargon, we haven't understood it well enough. You'll always know what's happening with your systems and why." },
  { icon: ShieldCheck, title: "Prevention over repair", desc: "We'd rather stop a problem at 2am than invoice you for fixing it at 9am. Monitoring, patching and tested backups come before everything else." },
  { icon: HeartHandshake, title: "No lock-ins, no games", desc: "Month-to-month agreements and full documentation of your environment. If we ever stop earning your business, you're free to leave with everything." },
];

const About = () => (
  <>
    <SEO title="About UMI IT Services | Adelaide IT Support Team" description="Meet UMI IT Services: an Adelaide MSP combining enterprise-grade engineering with small-business warmth. Plain English, no lock-in contracts." />

    <section className="relative overflow-hidden bg-[#0B2545] pt-28 pb-16 md:pt-36 md:pb-24">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(55% 45% at 75% 25%, rgba(23,184,166,0.15), transparent 70%)" }} />
      <div className="pointer-events-none absolute inset-0 hero-grain opacity-[0.05]" />
      <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}
            className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">About UMI</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.12, ease: EASE }}
            className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.1] text-white">
            Big-business IT capability, <span className="text-[#17B8A6]">local Adelaide care.</span>
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
            className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
            UMI IT Services exists for one reason: Adelaide small and medium businesses deserve the same calibre of IT support that large enterprises get — delivered by people who answer the phone, speak plainly, and know your name.
          </motion.p>
        </div>
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.35, ease: EASE }} className="relative">
          <div className="absolute -inset-0 translate-x-4 translate-y-4 rounded-2xl border-2 border-[#17B8A6]/40" aria-hidden="true" />
          <img
            src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
            alt="The UMI IT Services team planning a client project in their Adelaide office"
            className="relative rounded-2xl shadow-2xl w-full object-cover max-h-[70vh] lg:max-h-[440px]"
          />
        </motion.div>
      </div>
    </section>

    <Marquee items={["Adelaide-Based", "Plain English", "No Lock-Ins", "Enterprise-Grade", "Small-Business Warmth", "Proactive Support"]} />

    {/* Founders */}
    <section data-testid="founders-section" className="py-16 md:py-24 bg-white">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">The people behind UMI</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545] max-w-3xl">Meet the founders</h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed max-w-3xl">
            UMI was founded by a small Adelaide team whose combined experience spans hands-on systems engineering, cybersecurity and Essential Eight compliance, and custom software delivery.
          </p>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {founders.map((f, i) => (
            <Reveal key={f.testid} delay={i * 0.1} className="h-full">
              <div data-testid={f.testid} className="h-full rounded-2xl border border-slate-200 bg-[#F8FAFC] p-6 sm:p-8">
                <img
                  src={f.photo}
                  alt={`${f.name}, ${f.role.split("·")[0].trim()} at UMI IT Services`}
                  className="h-24 w-24 rounded-full object-cover object-top ring-4 ring-[#CCFBF1]"
                  loading="lazy"
                />
                <h3 className="mt-5 text-xl font-semibold text-[#0B2545]">{f.name}</h3>
                <p className="mt-1 text-sm font-semibold text-[#0D9488]">{f.role}</p>
                <p className="mt-3 text-base text-slate-600 leading-relaxed">{f.bio}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="py-16 md:py-24 bg-[#F1F5F9]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">How we work</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545]">Three rules we don't break</h2>
        </Reveal>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08} className="h-full">
              <div className="h-full rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
                <div className="w-12 h-12 rounded-xl bg-[#0B2545] flex items-center justify-center">
                  <v.icon size={22} className="text-[#17B8A6]" aria-hidden="true" />
                </div>
                <h3 className="mt-5 text-lg font-semibold text-[#0B2545]">{v.title}</h3>
                <p className="mt-2 text-base text-slate-600 leading-relaxed">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Service area */}
    <section className="py-16 md:py-24 bg-white">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#17B8A6]">Where we work</p>
          <h2 className="mt-3 text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0B2545]">Proudly Adelaide-based, metro-wide</h2>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Our helpdesk and engineers are based in Adelaide — not routed through an offshore call centre. When remote support isn't enough, we come to you.
          </p>
          <ul className="mt-6 grid grid-cols-2 gap-3 text-base text-slate-700">
            {["Adelaide CBD", "North Adelaide", "Norwood & the East", "Unley & the South", "Mawson Lakes & the North", "West Lakes & the coast"].map((area) => (
              <li key={area} className="flex items-center gap-2">
                <MapPin size={15} className="shrink-0 text-[#17B8A6]" aria-hidden="true" /> {area}
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="relative">
            <div className="absolute -inset-0 translate-x-3 translate-y-3 rounded-2xl border-2 border-[#17B8A6]/40" aria-hidden="true" />
            <img
              src="https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Secure server infrastructure monitored by UMI for Adelaide small businesses"
              className="relative rounded-2xl shadow-lg w-full object-cover max-h-[70vh] lg:max-h-[400px]"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <section className="relative overflow-hidden bg-[#0B2545] py-16 md:py-20">
      <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(50% 60% at 50% 100%, rgba(23,184,166,0.14), transparent 70%)" }} />
      <div className="relative px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
        <Reveal>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white">Let's talk about your IT</h2>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto">A free 30-minute consultation is the easiest way to find out if we're the right fit for your business.</p>
        </Reveal>
        <Reveal delay={0.12}>
          <MotionLink to="/contact" data-testid="about-cta-consultation"
            whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            className="mt-8 inline-flex items-center gap-2 bg-[#17B8A6] hover:bg-[#0D9488] text-white font-semibold px-8 py-4 rounded-lg transition-colors duration-200">
            Book a Free Consultation <ArrowRight size={18} aria-hidden="true" />
          </MotionLink>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
